<template>
  <transition name="dialog-fade">
    <div v-if="visible" class="dialog-overlay">
      <div class="camera-capture-box">
        <div v-if="error" class="capture-error">
          <div class="dialog-icon">!</div>
          <h2 class="dialog-title">Camera error</h2>
          <p class="dialog-body">{{ error }}</p>
          <div class="dialog-actions">
            <button class="dialog-btn dialog-btn--confirm" @click="close">Close</button>
          </div>
        </div>
        <template v-else>
          <video ref="video" class="camera-preview" autoplay playsinline></video>
          <canvas ref="canvas" class="capture-canvas"></canvas>
          <div class="capture-actions">
            <button class="dialog-btn dialog-btn--cancel" @click="close">Cancel</button>
            <button class="dialog-btn dialog-btn--confirm" @click="takePhoto">Take photo</button>
          </div>
        </template>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      stream: null,
      error: null
    };
  },
  watch: {
    visible(val) {
      if (val) {
        this.startCamera();
      } else {
        this.stopCamera();
      }
    }
  },
  methods: {
    startCamera() {
      this.error = null;
      navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } })
        .then(stream => {
          this.stream = stream;
          this.$nextTick(() => {
            if (this.$refs.video) {
              this.$refs.video.srcObject = stream;
            }
          });
        })
        .catch(err => {
          if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
            this.error = 'No camera found on this device. Try browsing for an image instead.';
          } else if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
            this.error = 'Camera permission was denied. Please allow camera access in your browser settings and try again.';
          } else {
            this.error = 'Could not access the camera. Try browsing for an image instead.';
          }
        });
    },
    stopCamera() {
      if (this.stream) {
        this.stream.getTracks().forEach(t => t.stop());
        this.stream = null;
      }
      this.error = null;
    },
    takePhoto() {
      const video = this.$refs.video;
      const canvas = this.$refs.canvas;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext('2d').drawImage(video, 0, 0);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
      this.stopCamera();
      this.$emit('captured', dataUrl, video.videoWidth, video.videoHeight);
    },
    close() {
      this.stopCamera();
      this.$emit('cancel');
    }
  },
  beforeDestroy() {
    this.stopCamera();
  }
}
</script>

<style scoped>
.dialog-overlay {
  position: fixed;
  inset: 0;
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 8, 7, 0.6);
}

.camera-capture-box {
  background-color: var(--surface-dark);
  border: 2px solid var(--text-bright);
  border-radius: 4px;
  padding: 24px;
  max-width: 480px;
  width: 90%;
  filter: url(#button-stroke);
  box-shadow: 0 8px 32px rgba(0, 8, 7, 0.5);
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.camera-preview {
  width: 100%;
  border-radius: 2px;
  display: block;
  background-color: var(--surface-darkest);
}

.capture-canvas {
  display: none;
}

.capture-actions {
  display: flex;
  gap: 16px;
  justify-content: flex-start;
}

.capture-error {
  padding: 8px 4px 0;
}

.dialog-icon {
  text-align: center;
  font-family: var(--font-family-balsamiq);
  font-size: 32px;
  color: var(--text-bright);
  margin-bottom: 8px;
}

.dialog-title {
  font-family: Cabin-Sketch, cursive;
  font-weight: bold;
  font-size: 24px;
  line-height: 1.2;
  color: var(--text-lightest);
  text-transform: uppercase;
  text-align: center;
  margin: 0 0 12px;
}

.dialog-body {
  font-family: var(--font-family-balsamiq);
  font-size: 14px;
  line-height: 1.5;
  color: var(--text-lightest);
  margin: 0 0 24px;
}

.dialog-actions {
  display: flex;
  gap: 16px;
}

.dialog-btn {
  font-family: var(--font-family-balsamiq);
  font-size: 14px;
  line-height: 24px;
  padding: 6px 14px;
  border-radius: 1px;
  cursor: pointer;
  border: 2px solid var(--text-bright);
  background-color: transparent;
  color: var(--text-bright);
  filter: url(#button-stroke);
  white-space: nowrap;
}

.dialog-btn--cancel {
  border-color: transparent;
  color: var(--text-bright);
  text-decoration: underline;
  text-decoration-skip-ink: none;
  filter: none;
}

.dialog-btn--confirm {
  background-color: var(--surface-darkest);
  color: var(--text-bright);
}

.dialog-fade-enter-active,
.dialog-fade-leave-active {
  transition: opacity 0.2s ease;
}
.dialog-fade-enter,
.dialog-fade-leave-to {
  opacity: 0;
}
.dialog-fade-enter-active .camera-capture-box,
.dialog-fade-leave-active .camera-capture-box {
  transition: transform 0.2s ease;
}
.dialog-fade-enter .camera-capture-box {
  transform: scale(0.95);
}
</style>
